import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useTheme } from '../contexts/ThemeContext';
import { organizationService } from '../services/organization';
import { Building2, Search, Phone, MapPin, ChevronRight, UtensilsCrossed, Plus, X } from 'lucide-react';
import { clsx } from 'clsx';
import { PageSkeleton, EmptyState } from '../components/LoadingStates';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

export const OrganizationOnboarding: React.FC = () => {
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const p = currentTheme.colors.primary;
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', address: '', mobile: '', email: '' });

  const handleCreate = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      toast.error('Code and Name are required');
      return;
    }
    setSaving(true);
    try {
      await organizationService.createOrganization({
        code: form.code.trim(),
        name: form.name.trim(),
        address: form.address.trim() || undefined,
        mobile: form.mobile.trim() || undefined,
        email: form.email.trim() || undefined,
      });
      toast.success('Organization created successfully');
      queryClient.invalidateQueries(['organizations-onboarding']);
      setShowModal(false);
      setForm({ code: '', name: '', address: '', mobile: '', email: '' });
    } catch {
      toast.error('Failed to create organization');
    } finally {
      setSaving(false);
    }
  };

  const { data: organizations = [], isLoading } = useQuery(
    ['organizations-onboarding'],
    organizationService.getOrganizations
  );

  const filteredOrgs = organizations.filter(o =>
    o.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (o.code && o.code.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (isLoading) return <PageSkeleton type="grid" />;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center" style={{ color: currentTheme.colors.text.primary }}>
            <Building2 className="h-6 w-6 mr-2" style={{ color: p[600] }} />
            Organizations
          </h1>
          <p style={{ color: currentTheme.colors.text.secondary }}>Select an organization to manage it</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white flex-shrink-0"
          style={{ backgroundColor: p[600] }}>
          <Plus className="h-4 w-4" />
          New Organization
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input type="text" placeholder="Search organizations..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none"
            style={{ borderColor: currentTheme.colors.border.primary }} />
        </div>
      </div>

      {/* Cards */}
      {filteredOrgs.length === 0 ? (
        <EmptyState
          icon={<Building2 className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />}
          title="No Organizations Found"
          description={searchTerm ? 'Try adjusting your search.' : 'No organizations available.'}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredOrgs.map(org => (
            <div key={org.id}
              className="rounded-xl border shadow-sm overflow-hidden cursor-pointer group transition-shadow hover:shadow-md"
              style={{ borderColor: currentTheme.colors.border.primary, backgroundColor: '#fff' }}
              onClick={() => navigate(`/organization-onboarding/${org.id}`)}>

              {/* Card header */}
              <div className="px-5 py-4" style={{ backgroundColor: p[50] }}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-2 rounded-lg flex-shrink-0" style={{ backgroundColor: p[100] }}>
                      <Building2 className="h-5 w-5" style={{ color: p[600] }} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold truncate" style={{ color: currentTheme.colors.text.primary }}>{org.name}</h3>
                      {org.code && (
                        <span className="text-xs px-1.5 py-0.5 rounded font-mono" style={{ backgroundColor: p[100], color: p[700] }}>{org.code}</span>
                      )}
                    </div>
                  </div>
                  <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ml-2', org.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600')}>
                    {org.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              {/* Card body */}
              <div className="px-5 py-3">
                <div className="space-y-1 mb-3">
                  {org.address && (
                    <div className="flex items-center gap-2 text-xs" style={{ color: currentTheme.colors.text.secondary }}>
                      <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                      <span className="truncate">{org.address}</span>
                    </div>
                  )}
                  {org.mobile && (
                    <div className="flex items-center gap-2 text-xs" style={{ color: currentTheme.colors.text.secondary }}>
                      <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                      <span>{org.mobile}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between pt-2 border-t" style={{ borderColor: currentTheme.colors.border.primary }}>
                  <div className="flex items-center gap-1 text-xs" style={{ color: currentTheme.colors.text.secondary }}>
                    <UtensilsCrossed className="h-3.5 w-3.5" />
                    <span>Restaurants, Employees &amp; Roles</span>
                  </div>
                  <ChevronRight className="h-4 w-4 opacity-40 group-hover:opacity-100 transition-opacity" style={{ color: p[600] }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Organization Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl shadow-xl" style={{ backgroundColor: currentTheme.colors.background.card }}>
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: currentTheme.colors.border.primary }}>
              <h2 className="text-lg font-semibold" style={{ color: currentTheme.colors.text.primary }}>New Organization</h2>
              <button onClick={() => setShowModal(false)} className="p-1 rounded hover:bg-gray-100">
                <X className="h-5 w-5" style={{ color: currentTheme.colors.text.secondary }} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4 space-y-4">
              {[
                { label: 'Code *', key: 'code', placeholder: 'e.g. ORG001' },
                { label: 'Name *', key: 'name', placeholder: 'Organization name' },
                { label: 'Address', key: 'address', placeholder: 'Optional' },
                { label: 'Mobile', key: 'mobile', placeholder: 'Optional' },
                { label: 'Email', key: 'email', placeholder: 'Optional' },
              ].map(({ label, key, placeholder }) => (
                <div key={key}>
                  <label className="block text-sm font-medium mb-1" style={{ color: currentTheme.colors.text.primary }}>{label}</label>
                  <input
                    type="text"
                    placeholder={placeholder}
                    value={form[key as keyof typeof form]}
                    onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none"
                    style={{ borderColor: currentTheme.colors.border.primary, color: currentTheme.colors.text.primary }}
                  />
                </div>
              ))}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t" style={{ borderColor: currentTheme.colors.border.primary }}>
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 rounded-lg text-sm border"
                style={{ borderColor: currentTheme.colors.border.primary, color: currentTheme.colors.text.secondary }}>
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={saving}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60"
                style={{ backgroundColor: p[600] }}>
                {saving ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
